import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { ErrorState, LoadingState } from "../components/LoadingState";
import { Modal } from "../components/Modal";
import { TransactionForm } from "../components/TransactionForm";
import { TransactionList } from "../components/TransactionList";
import type { Transaction, TransactionInput } from "../types";
import { formatCurrency } from "../utils/currency";
import { formatChineseDate, isValidDateKey } from "../utils/date";

export function DayPage() {
  const { date = "" } = useParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState<string>();

  const loadTransactions = useCallback(async () => {
    if (!isValidDateKey(date)) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.getTransactions(date);
      setTransactions(response.transactions);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "账目加载失败。");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    void loadTransactions();
    const refresh = () => void loadTransactions();
    window.addEventListener("ledger-data-changed", refresh);
    return () => window.removeEventListener("ledger-data-changed", refresh);
  }, [loadTransactions]);

  const summary = useMemo(() => transactions.reduce(
    (total, item) => {
      total[item.type] += item.amount_cents;
      return total;
    },
    { expense: 0, income: 0 },
  ), [transactions]);

  if (!isValidDateKey(date)) return <Navigate to="/" replace />;

  const closeForm = () => {
    setEditing(null);
    setShowForm(false);
  };

  const saveTransaction = async (input: TransactionInput) => {
    if (editing) await api.updateTransaction(editing.id, input);
    else await api.createTransaction(input);
    closeForm();
    await loadTransactions();
    window.dispatchEvent(new Event("ledger-data-changed"));
  };

  const deleteTransaction = async (transaction: Transaction) => {
    if (!window.confirm("确定删除这条账目吗？")) return;
    setDeletingId(transaction.id);
    try {
      await api.deleteTransaction(transaction.id);
      await loadTransactions();
      window.dispatchEvent(new Event("ledger-data-changed"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "删除失败，请稍后重试。");
    } finally {
      setDeletingId(undefined);
    }
  };

  return (
    <main className="page day-page">
      <Link className="back-link" to="/"><ArrowLeft size={18} />返回日历</Link>
      <section className="day-heading">
        <div>
          <p className="eyebrow">每日明细</p>
          <h1>{formatChineseDate(date)}</h1>
        </div>
        <button className="primary-button add-button" onClick={() => setShowForm(true)}><Plus size={18} />新增账目</button>
      </section>

      <section className="day-summary" aria-label="今日收支汇总">
        <div><span>今日支出</span><strong className="expense">{formatCurrency(summary.expense)}</strong></div>
        <div><span>今日收入</span><strong className="income">{formatCurrency(summary.income)}</strong></div>
        <div className="balance"><span>今日结余</span><strong>{formatCurrency(summary.income - summary.expense)}</strong></div>
      </section>

      <section className="transactions-section">
        <div className="section-title"><h2>全部账目</h2><span>{transactions.length} 笔</span></div>
        {loading ? <LoadingState label="正在读取当天账目…" /> : error ? <ErrorState message={error} onRetry={() => void loadTransactions()} /> : (
          <TransactionList transactions={transactions} onEdit={(item) => setEditing(item)} onDelete={(item) => void deleteTransaction(item)} deletingId={deletingId} />
        )}
      </section>

      {(showForm || editing) && (
        <Modal title={editing ? "编辑账目" : "新增账目"} onClose={closeForm}>
          <TransactionForm date={date} transaction={editing ?? undefined} onSubmit={saveTransaction} onCancel={closeForm} />
        </Modal>
      )}
    </main>
  );
}
